import { apiClient } from '@/shared/api/client';
import type { ApiResponse } from '@/shared/types/api.types';
import type {
  Verification,
  CreateVerificationRequest,
} from '@/features/verifications/types/verification.types';

type Method = 'GET' | 'POST' | 'DELETE';

const authBase = (experienceId: string): string =>
  `/candidate/experiences/${experienceId}/verifications`;
const publicBase = (shareId: string, experienceId: string): string =>
  `/public/candidates/${shareId}/experiences/${experienceId}/verifications`;

/** The only place that assembles raw URL + method + body for the verification endpoints. */
async function request<T>(path: string, method: Method, body?: unknown): Promise<T> {
  const options = body === undefined ? { method } : { method, body };
  return (await apiClient<ApiResponse<T>>(path, options)).data;
}

const discard = async (pending: Promise<unknown>): Promise<void> => {
  await pending;
};

export const candidateVerificationService = {
  list: (experienceId: string): Promise<Verification[]> => request(authBase(experienceId), 'GET'),

  create: (experienceId: string, body: CreateVerificationRequest): Promise<Verification> =>
    request(authBase(experienceId), 'POST', body),

  delete: (experienceId: string, verificationId: string): Promise<void> =>
    discard(request(`${authBase(experienceId)}/${verificationId}`, 'DELETE')),

  send: (experienceId: string, verificationId: string): Promise<Verification> =>
    request(`${authBase(experienceId)}/${verificationId}/send`, 'POST'),

  listByShareId: (shareId: string, experienceId: string): Promise<Verification[]> =>
    request(publicBase(shareId, experienceId), 'GET'),

  createByShareId: (
    shareId: string,
    experienceId: string,
    body: CreateVerificationRequest
  ): Promise<Verification> => request(publicBase(shareId, experienceId), 'POST', body),

  deleteByShareId: (shareId: string, experienceId: string, verificationId: string): Promise<void> =>
    discard(request(`${publicBase(shareId, experienceId)}/${verificationId}`, 'DELETE')),

  sendByShareId: (
    shareId: string,
    experienceId: string,
    verificationId: string
  ): Promise<Verification> =>
    request(`${publicBase(shareId, experienceId)}/${verificationId}/send`, 'POST'),
};
